import { StyleSheet, TouchableOpacity, ViewStyle } from "react-native"

import { router } from "expo-router"
import { usePostHog } from "posthog-react-native"

import { Icon } from "src/components/Ignite/Icon"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

interface LibraryButtonProps {
  /** Fires the `auth_library_button_pressed` PostHog event */
  onPress?: () => void
  /**
   * When true, renders as a 40×40 flat icon button (no circular background)
   * for placement inside the CompositeInputCluster button row as a modeToggle.
   */
  compact?: boolean
}

/**
 * Library icon button. In default mode it renders as a standalone 56×56 pill
 * (for use outside the cluster). In compact mode it renders as a flat 40×40
 * icon button for placement as a modeToggle inside the cluster button row.
 */
export function LibraryButton({ onPress: externalOnPress, compact = false }: LibraryButtonProps) {
  const { themed, theme } = useAppTheme()
  const posthog = usePostHog()

  const handlePress = () => {
    posthog?.capture("auth_library_button_pressed")
    if (externalOnPress) {
      externalOnPress()
    } else {
      router.push("/(app)/content-library" as any)
    }
  }

  if (compact) {
    return (
      <TouchableOpacity
        style={styles.compactBtn}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel="Content Library"
        accessibilityHint="Opens the content library"
      >
        <Icon icon="library" size={22} color={theme.colors.buttonText} />
      </TouchableOpacity>
    )
  }

  return (
    <TouchableOpacity
      style={themed($button)}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel="Content Library"
      accessibilityHint="Opens the content library"
    >
      <Icon icon="library" size={22} color={theme.colors.buttonText} />
    </TouchableOpacity>
  )
}

const $button: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 56,
  height: 56,
  borderRadius: 9999,
  backgroundColor: colors.buttonBackground,
  alignItems: "center",
  justifyContent: "center",
})

const styles = StyleSheet.create({
  compactBtn: {
    width: 40,
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
})
